import React, { useEffect, useState } from 'react';
import { Wand2 } from 'lucide-react';
import { Button } from './UI';
import { useData } from '../App';
import { api } from '../services/api';
import { PickLine } from '../types';
import { errorText, Money, money, Td, Th, useNames } from './billing/shared';

// «Подобрать объёмы» в карточке выплаты самозанятому («Операции»): строки задания из квоты ИП по объекту,
// итог равен сумме выплаты (хвост — «Расходные материалы…» по 1 ₽). Раньше — отдельная вкладка в «Объёмах работ».
export type VolumeLine = PickLine & { qtyText: string };

const qtyText = (n: number | null | undefined) => (n ?? 0).toLocaleString('ru-RU', { maximumFractionDigits: 3 });

// Есть ли у объекта прайс подрядчика — значит, выплата по нему оформляется заданием с объёмами
export const useVolumeProject = (projectId: string, orgId: string) => {
    const [active, setActive] = useState(false);
    useEffect(() => {
        setActive(false);
        if (!projectId || !orgId) return;
        let alive = true;
        api.volumePool(projectId, orgId)
            .then(rows => { if (alive) setActive(rows.some(r => r.role !== 'BUFFER' && r.contractorPrice > 0)); })
            .catch(() => {});
        return () => { alive = false; };
    }, [projectId, orgId]);
    return active;
};

export const volumeProblems = (lines: VolumeLine[] | null, target: number): string | null => {
    if (!lines) return 'Нажмите «Подобрать объёмы»';
    const over = lines.filter(l => l.role !== 'BUFFER' && l.available != null && l.qty > l.available + 1e-6);
    if (over.length) return `Больше доступного остатка: ${over.map(l => l.name).join(', ')}`;
    const total = Math.round(lines.reduce((s, l) => s + l.amount, 0) * 100) / 100;
    if (Math.abs(total - target) > 0.004) return `Сумма строк ${money(total)} ₽ не равна сумме выплаты ${money(target)} ₽`;
    return null;
};

export const VolumePick: React.FC<{ projectId: string; orgId: string; target: number; lines: VolumeLine[] | null;
                                    onChange: (lines: VolumeLine[] | null) => void }> = ({ projectId, orgId, target, lines, onChange }) => {
    const { showNotification } = useData();
    const names = useNames();
    const [availableAmount, setAvailableAmount] = useState<number | null>(null);

    const pick = async () => {
        if (target <= 0) { showNotification('Укажите сумму выплаты', 'error'); return; }
        try {
            const r = await api.pickVolumes({ projectId, organizationId: orgId, amount: target });
            onChange(r.lines.map(l => ({ ...l, qtyText: String(l.qty) })));
            setAvailableAmount(r.availableAmount);
        } catch (e) { showNotification(errorText(e), 'error'); }
    };
    // Ручная правка количества: буферная строка держит итог равным сумме выплаты
    const setQty = (i: number, v: string) => {
        if (!lines) return;
        const next = lines.map((l, j) => j === i ? { ...l, qtyText: v, qty: Number(v) || 0, amount: Math.round((Number(v) || 0) * l.price * 100) / 100 } : l);
        const bi = next.findIndex(l => l.role === 'BUFFER');
        const other = next.reduce((s, l, j) => j === bi ? s : s + l.amount, 0);
        const tail = Math.round((target - other) * 100) / 100;
        if (bi >= 0) next[bi] = { ...next[bi], qty: tail, qtyText: String(tail), amount: tail };
        else if (tail > 0) next.push({ code: 'consumables', name: 'Расходные материалы и монтажные приспособления', unit: 'усл.', qty: tail,
                                       qtyText: String(tail), price: 1, amount: tail, role: 'BUFFER', available: null });
        onChange(next);
    };
    const total = lines ? Math.round(lines.reduce((s, l) => s + l.amount, 0) * 100) / 100 : 0;
    const buffer = lines?.find(l => l.role === 'BUFFER');
    const stale = lines && Math.abs(total - target) > 0.004;

    return (
        <div className="border rounded-lg border-slate-300 overflow-hidden">
            <div className="bg-slate-100 p-1.5 border-b border-slate-200 flex items-center justify-between gap-2">
                <span className="font-semibold text-[10px] text-slate-600">Объёмы по объекту — из квоты {names.orgShort(orgId)}</span>
                <Button type="button" size="sm" variant="outline" onClick={pick}><Wand2 size={12} className="mr-1" /> Подобрать объёмы</Button>
            </div>
            {!lines && <p className="p-2 text-[11px] text-slate-500">Подберите позиции под сумму выплаты: по ним будут созданы задание и акт,
                объёмы спишутся из квоты ИП, выплата уйдёт в зарплатный модуль CRM.</p>}
            {lines && (
                <div className="p-2 space-y-1.5">
                    {availableAmount != null && availableAmount < target &&
                        <p className="text-[11px] text-red-600">В квоте {names.orgShort(orgId)} по объекту объёмов на {money(availableAmount)} ₽ — меньше суммы выплаты.</p>}
                    {stale && <p className="text-[11px] text-amber-700">Сумма выплаты изменилась — подберите объёмы заново.</p>}
                    {buffer && buffer.amount > 1000 && <p className="text-[11px] text-amber-700">Хвост «Расходные материалы…» {money(buffer.amount)} ₽ — больше обычного: проверьте объёмы.</p>}
                    <table className="w-full text-xs">
                        <thead className="text-slate-500"><tr><Th>Позиция</Th><Th right>Доступно</Th><Th right>Кол-во</Th><Th right>Цена</Th><Th right>Сумма</Th></tr></thead>
                        <tbody>{lines.map((l, i) => {
                            const over = l.role !== 'BUFFER' && l.available != null && l.qty > l.available + 1e-6;
                            return (
                                <tr key={l.code} className="border-t border-slate-100">
                                    <Td>{l.name}</Td><Td right className="text-slate-400">{l.available == null ? '—' : qtyText(l.available)}</Td>
                                    <Td right className="whitespace-nowrap">{l.role === 'BUFFER' ? qtyText(l.qty) :
                                        <input type="number" min="0" step="1" className={`w-16 rounded border px-1 py-0.5 text-right ${over ? 'border-red-500' : 'border-slate-300'}`}
                                               value={l.qtyText} onChange={e => setQty(i, e.target.value)} />} {l.unit}</Td>
                                    <Td right><Money value={l.price} /></Td><Td right><Money value={l.amount} /></Td>
                                </tr>);
                        })}
                            <tr className="border-t font-semibold"><Td colSpan={4}>Итого (акт)</Td><Td right><Money value={total} /></Td></tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
